import cn from 'classnames'
import { motion } from 'framer-motion'
import * as R from 'ramda'
import React, { useEffect, useRef } from 'react'
import type { DragLayerMonitor } from 'react-dnd'
import { useDragLayer, useDrop } from 'react-dnd'
import { createPortal } from 'react-dom'

import { useDragScroll } from 'components/uiKit/useDragScroll'
import { ScrollContainerEnum } from 'services/Scroll/enums'

import { KitSize } from '../KitTypes'
import * as s from './ArrayControl.module.scss'
import { getDragItemType, NAVIGATION_GAP } from './constants'
import type {
  ArrayValue,
  FieldArrayDragItemType,
  IArrayChangeMeta,
  IRenderItemProps,
} from './types'

const getItemStyles = (
  currentOffset?: { x: number; y: number } | null,
  container?: HTMLDivElement | null,
  node?: HTMLDivElement | null,
  direction?: 'vertical' | 'horizontal',
): React.CSSProperties => {
  if (!currentOffset || !container || !node) {
    return {
      display: 'none',
    }
  }
  if (direction === 'vertical') {
    const { width, height, left } = node?.getBoundingClientRect() || {}
    const { y: cY, height: cH } = container.getBoundingClientRect()

    const minY = cY + NAVIGATION_GAP
    const maxY = cY + cH - NAVIGATION_GAP - height
    const y = R.clamp(minY, maxY, currentOffset.y)

    return {
      position: 'fixed',
      top: 0,
      pointerEvents: 'none',
      zIndex: 10000,
      width,
      height,
      left,
      transform: `translate(${20}px, ${y}px)`,
    }
  }
  if (direction === 'horizontal') {
    const { width, height, top } = node?.getBoundingClientRect() || {}
    const { x: cX, width: cW } = container.getBoundingClientRect()

    const minX = cX + NAVIGATION_GAP
    const maxX = cX + cW - NAVIGATION_GAP - width
    const x = R.clamp(minX, maxX, currentOffset.x)

    return {
      position: 'fixed',
      left: 0,
      pointerEvents: 'none',
      zIndex: 10000,
      width,
      height,
      top,
      transform: `translate(${x}px, ${20}px)`,
    }
  }
  return {}
}

interface IFieldArrayDragLayoutProps<V extends ArrayValue> {
  onDrop: (id: string) => void
  children: React.ReactNode
  onChange?: (value: V[], meta?: IArrayChangeMeta<V>) => void
  RenderItem: React.FC<IRenderItemProps<V>>
  value: V[]
  max?: number
  min?: number
  name: string
  direction: 'vertical' | 'horizontal'
  size?: KitSize
}

const FieldArrayDragLayout = <V extends ArrayValue>({
  onDrop,
  children,
  RenderItem,
  min,
  max,
  value,
  onChange,
  name,
  direction,
  size,
  ...rest
}: IFieldArrayDragLayoutProps<V>) => {
  const isActiveDrag = value.length > 1
  const ref = useRef<HTMLDivElement>(null)
  const [, drop] = useDrop<FieldArrayDragItemType<V>>({
    accept: getDragItemType(name),
    drop: (item) => item?.item && onDrop(item?.item?.value),
  })
  const handlers = useDragScroll(ScrollContainerEnum.toolbar)

  const { isDragging, item, index, itemRef, clientOffset } = useDragLayer(
    (monitor: DragLayerMonitor<FieldArrayDragItemType<V>>) => ({
      isDragging:
        monitor.getItemType() === getDragItemType(name) && monitor.isDragging() && isActiveDrag,
      clientOffset: monitor.getSourceClientOffset(),
      item: monitor.getItem()?.item,
      itemRef: monitor.getItem()?.itemRef,
      index: monitor.getItem()?.index,
    }),
  )

  // delay when dropped outside drop element https://github.com/react-dnd/react-dnd/issues/3115#issuecomment-1220682935
  useEffect(() => {
    drop(document.body)
    return () => {
      drop(null)
    }
  }, [drop])

  return (
    <div {...handlers} className={cn(s.root, s[direction])} ref={ref}>
      {children}
      {isDragging &&
        createPortal(
          <motion.div
            layoutDependency={item}
            layoutId={item?.value}
            style={getItemStyles(clientOffset, ref.current, itemRef, direction)}
          >
            {item && (
              <RenderItem
                {...rest}
                index={index ?? 0}
                item={item}
                max={max}
                min={min}
                name={name}
                onChange={onChange}
                value={value}
              />
            )}
          </motion.div>,
          document.body,
        )}
    </div>
  )
}

export default React.memo(FieldArrayDragLayout) as typeof FieldArrayDragLayout
